/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  addEmailRestrictions,
  getEmailRestrictions,
} from "../../../../api/User";
import { UserEmailRestrictionsAtom } from "../../../../atoms/UserEmailRestrictionsAtom";
import StyledButton from "../../../StyledButton";
import StyledInput from "../../../StyledInput";
import { Select } from "@chakra-ui/react";
import { UserAtom } from "../../../../atoms/UserAtom";
import { IUserEmailRestrictions } from "../../../../generated/Api";

export enum SelectTab {
  BLOCK = "BLOCK",
  FLAG = "FLAG",
  SUSPEND = "SUSPEND",
}

export enum KeywordMatchMode {
  EXACT_MATCH = "EXACT_MATCH",
  CONTAINS = "CONTAINS",
  STARTS_WITH = "STARTS_WITH",
  ENDS_WITH = "ENDS_WITH",
}

export const MATCH_MODE_LABEL_MAP: { [index in KeywordMatchMode]: string } = {
  STARTS_WITH: "Starts With",
  CONTAINS: "Contains",
  ENDS_WITH: "Ends With",
  EXACT_MATCH: "Exact Match",
};

export default function BlockKeyword() {
  const [selectedKeywordType, setSelectedKeywordType] = useState<SelectTab>(
    SelectTab.BLOCK
  );
  const [selectedKeywordMatchMode, setSelectedKeywordMatchMode] =
    useState<KeywordMatchMode>(KeywordMatchMode.EXACT_MATCH);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const setEmailRestrictions = useSetRecoilState(UserEmailRestrictionsAtom);
  const [user, setUser] = useRecoilState(UserAtom);

  async function handleKeywordBlock(type: SelectTab) {
    setLoading(true);
    getEmailRestrictions();

    if (keyword === "") {
      toast("The keyword can't be empty", { type: "warning" });
      setLoading(false);
      return;
    }

    const blocks = {
      blocked: user.emailRestrictions.keywordsBlocked,
      flagged: user.emailRestrictions.keywordsFlagged,
      suspended: user.emailRestrictions.keywordsSuspended,

      partialKeywordsBlocked: user.emailRestrictions.partialKeywordsBlocked,
      partialKeywordsFlagged: user.emailRestrictions.partialKeywordsFlagged,
      partialKeywordsSuspended: user.emailRestrictions.partialKeywordsSuspended,
    };

    let keywordAlreadyExists = false;

    if (
      (blocks.blocked.find((b) => b === keyword) ||
        blocks.partialKeywordsBlocked?.find((b) => b.keyword === keyword)) &&
      type !== "BLOCK"
    )
      keywordAlreadyExists = true;

    if (
      (blocks.suspended.find((s) => s === keyword) ||
        blocks.partialKeywordsSuspended?.find((s) => s.keyword === keyword)) &&
      type !== "SUSPEND"
    )
      keywordAlreadyExists = true;

    if (
      (blocks.flagged.find((f) => f === keyword) ||
        blocks.partialKeywordsFlagged?.find((f) => f.keyword === keyword)) &&
      type !== "FLAG"
    )
      keywordAlreadyExists = true;

    if (keywordAlreadyExists) {
      toast("The keyword is already blocked with another type", {
        type: "warning",
      });
      setLoading(false);
      return;
    }

    // Mapping of the property used on the patch
    const BLOCK_PROPERTY_MAP: {
      [type in SelectTab]: keyof IUserEmailRestrictions;
    } = {
      BLOCK: "keywordsBlocked",
      FLAG: "keywordsFlagged",
      SUSPEND: "keywordsSuspended",
    };

    // These are partial blocks, so we need to put them on their partial counterparts
    const BLOCK_PROPERTY_WITH_MODE_MAP: {
      [type in SelectTab]: keyof IUserEmailRestrictions;
    } = {
      BLOCK: "partialKeywordsBlocked",
      FLAG: "partialKeywordsFlagged",
      SUSPEND: "partialKeywordsSuspended",
    };

    // Mapping of the property used on the label, for the success toast
    const BLOCK_LABEL_MAP: { [type in SelectTab]: string } = {
      BLOCK: "Blocked",
      FLAG: "Flagged",
      SUSPEND: "Suspended",
    };

    try {
      // Old, exact match way
      if (selectedKeywordMatchMode === KeywordMatchMode.EXACT_MATCH) {
        let emailRestrictionProperty = BLOCK_PROPERTY_MAP[type];
        await addEmailRestrictions({ [emailRestrictionProperty]: [keyword] });
      } else {
        // New partial way
        let partialEmailRestrictionProperty =
          BLOCK_PROPERTY_WITH_MODE_MAP[type];
        await addEmailRestrictions({
          [partialEmailRestrictionProperty]: [
            { keyword, type: selectedKeywordMatchMode },
          ],
        });
      }

      setKeyword("");
      toast(`${BLOCK_LABEL_MAP[type]} keyword added`, { type: "success" });
      const emailRestrictions = await getEmailRestrictions();
      setEmailRestrictions(emailRestrictions);
      setUser({ ...user, emailRestrictions });
    } catch {
      toast("Error", { type: "error" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        handleKeywordBlock(selectedKeywordType);
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [keyword, selectedKeywordType]);

  return (
    <>
      <StyledInput
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        placeholder="Enter keyword"
      />

      <Select
        _focus={{ outline: "none" }}
        maxWidth="120px"
        minWidth="120px"
        height="40px"
        value={selectedKeywordType}
        onChange={(e) => setSelectedKeywordType(e.target.value as SelectTab)}
        css={css`
          font-size: 14px;
          letter-spacing: 0.16px;
        `}
      >
        <option value={SelectTab.BLOCK}>Block</option>
        <option value={SelectTab.FLAG}>Flag</option>
        <option value={SelectTab.SUSPEND}>Suspend</option>
      </Select>

      <Select
        _focus={{ outline: "none" }}
        maxWidth="140px"
        minWidth="140px"
        height="40px"
        value={selectedKeywordMatchMode}
        onChange={(e) =>
          setSelectedKeywordMatchMode(e.target.value as KeywordMatchMode)
        }
        css={css`
          font-size: 14px;
          letter-spacing: 0.16px;
        `}
      >
        {Object.values(KeywordMatchMode).map((mode) => (
          <option key={mode} value={mode}>
            {MATCH_MODE_LABEL_MAP[mode]}
          </option>
        ))}
      </Select>

      <StyledButton
        css={css`
          width: 112px;
          height: 40px;

          &::placeholder {
            font-size: 14px;
            letter-spacing: 0.16px;
            color: #979797;
          }
        `}
        isLoading={loading}
        onClick={() => {
          handleKeywordBlock(selectedKeywordType);
        }}
      >
        Block
      </StyledButton>
    </>
  );
}
